import { apiClient, getApiSessionIdentity } from '../shared/api-client';
import { getDurableItem, setDurableItem, getPersistentItem, setPersistentItem } from '../vrs-auth/storage';
let NativeModulesRef = null;
try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    NativeModulesRef = require('react-native').NativeModules;
}
catch {
    NativeModulesRef = null;
}
const NOTIFICATION_DEVICE_ID_STORAGE_KEY = 'malka_notification_device_id';
function randomId() {
    const globalRef = typeof globalThis !== 'undefined'
        ? globalThis
        : undefined;
    const cryptoRef = globalRef?.crypto;
    if (typeof cryptoRef?.randomUUID === 'function') {
        return cryptoRef.randomUUID();
    }
    return `device-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}
export function getOrCreateNotificationDeviceId() {
    const existing = getPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY);
    if (existing) {
        return existing;
    }
    const deviceId = randomId();
    setPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY, deviceId);
    return deviceId;
}
function detectPlatform() {
    const navigatorRef = typeof navigator !== 'undefined' ? navigator : undefined;
    const userAgent = navigatorRef?.userAgent || '';
    if (navigatorRef?.product === 'ReactNative') {
        return 'native';
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'ios';
    }
    if (/Android/i.test(userAgent)) {
        return 'android';
    }
    return 'web';
}
function currentNotificationPermission() {
    const globalRef = typeof globalThis !== 'undefined'
        ? globalThis
        : undefined;
    return globalRef?.Notification?.permission;
}
function nativeNotificationModule() {
    return NativeModulesRef?.VRSNotifications;
}
function urlBase64ToUint8Array(value) {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}
async function getWebPushSubscription(promptUser, guard) {
    const windowRef = typeof window !== 'undefined'
        ? window
        : undefined;
    const navigatorRef = typeof navigator !== 'undefined'
        ? navigator
        : undefined;
    if (guard && !guard.current())
        return null;
    if (!windowRef
        || typeof navigator === 'undefined'
        || !navigatorRef?.serviceWorker
        || !windowRef.PushManager
        || !windowRef.Notification) {
        return null;
    }
    let permission = windowRef.Notification.permission;
    if (permission === 'default' && promptUser) {
        permission = await windowRef.Notification.requestPermission();
    }
    if (guard && !guard.current())
        return null;
    if (permission !== 'granted') {
        return null;
    }
    const config = await apiClient.get('/api/notifications/web-push/public-key', { retryAuth: false, signal: guard?.signal });
    if (guard && !guard.current())
        return null;
    if (config.error || config.status !== 200 || config.data?.ready !== true || typeof config.data.publicKey !== 'string' || !config.data.publicKey) {
        return null;
    }
    const registration = await navigatorRef.serviceWorker.ready;
    if (guard && !guard.current())
        return null;
    const existing = await registration.pushManager.getSubscription();
    if (guard && !guard.current())
        return null;
    if (existing) {
        return existing;
    }
    return registration.pushManager.subscribe({
        applicationServerKey: urlBase64ToUint8Array(config.data.publicKey),
        userVisibleOnly: true
    });
}
async function nativePushOptions(promptUser, guard) {
    const module = nativeNotificationModule();
    // Old bridges expose only the interactive method. Never call it on reconnect.
    const request = promptUser ? module?.requestPushToken : module?.getPushToken;
    if (guard && !guard.current())
        return null;
    if (!request) {
        return null;
    }
    const result = await request.call(module);
    if (guard && !guard.current())
        return null;
    if (!result?.token) {
        return null;
    }
    return {
        appId: result.appId,
        platform: result.platform || detectPlatform(),
        pushProvider: result.pushProvider || (result.platform === 'ios' ? 'apns' : 'fcm'),
        pushToken: result.token
    };
}
async function webPushOptions(promptUser, guard) {
    const subscription = await getWebPushSubscription(promptUser, guard);
    if (!subscription) {
        return null;
    }
    return {
        platform: 'web',
        pushProvider: 'web-push',
        pushToken: JSON.stringify(subscription.toJSON())
    };
}
/** Automatic refresh preserves the server preference; only Settings supplies a reviewed revision. */
export function registerCurrentNotificationDevice(options = {}) {
    const session = getApiSessionIdentity();
    return new Promise(resolve => {
        const controller = new AbortController();
        let settled = false;
        const failed = () => ({ data: null, error: 'Notification device registration is not confirmed', status: 0 });
        const finish = (response) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            controller.abort();
            resolve(response);
        };
        const timer = setTimeout(() => finish(failed()), 20000);
        const current = () => !settled && Boolean(session) && session === getApiSessionIdentity();
        const guard = { current, signal: controller.signal };
        void (async () => {
            if (!current()) {
                finish(failed());
                return;
            }
            const stored = await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY);
            if (!current()) {
                finish(failed());
                return;
            }
            const cached = getNotificationDeviceId();
            if (stored && cached && stored !== cached) {
                finish(failed());
                return;
            }
            const deviceId = stored || cached || getOrCreateNotificationDeviceId();
            if (deviceId.length < 8 || deviceId.length > 160) {
                finish(failed());
                return;
            }
            setPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY, deviceId);
            await setDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY, deviceId);
            if (!current() || await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY) !== deviceId || !current()) {
                finish(failed());
                return;
            }
            const providerOptions = options.pushToken ? options : await nativePushOptions(false, guard)
                || await webPushOptions(false, guard) || options;
            if (!current()) {
                finish(failed());
                return;
            }
            const response = await apiClient.post('/api/notifications/devices', {
                appId: providerOptions.appId, deviceId, enabled: true,
                metadata: {
                    notificationPermission: currentNotificationPermission(),
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
                },
                platform: providerOptions.platform || detectPlatform(),
                pushProvider: providerOptions.pushProvider || (providerOptions.pushToken ? 'unknown' : 'local'),
                pushToken: providerOptions.pushToken, tenantId: options.tenantId
            }, { signal: controller.signal, retryAuth: false });
            finish(current() ? response : failed());
        })().catch(() => finish(failed()));
    });
}
/** Acquire a provider token without registering an account. Settings checks ownership afterwards. */
export async function requestNotificationPushOptions(guard) {
    return await nativePushOptions(true, guard) || await webPushOptions(true, guard);
}
/**
 * Meeting reminders need ordinary notifications. iOS keeps those on a second
 * token beside the PushKit call token, registered here under the same device
 * id; `prompt` (turning reminders on) may show the system prompt, reconnects
 * never do. Android's one FCM token already serves both, so asking there only
 * requests the notification permission and refreshes that registration.
 */
export async function registerAlertNotificationToken(prompt = false) {
    const module = nativeNotificationModule();
    const session = getApiSessionIdentity();
    const current = () => Boolean(session) && session === getApiSessionIdentity();
    if (!module || !current())
        return false;
    if (!module.getAlertPushToken && !module.requestAlertPushToken) {
        if (!prompt || !module.requestPushToken)
            return false;
        const response = await registerCurrentNotificationDevice(await nativePushOptions(true) || {});
        return !response.error;
    }
    const request = prompt ? module.requestAlertPushToken : module.getAlertPushToken;
    const deviceId = await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY) || getNotificationDeviceId();
    if (!request || !deviceId || !current())
        return false;
    let result;
    try {
        result = await request.call(module);
    }
    catch {
        return false;
    }
    if (!result?.token || !current())
        return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
        const response = await apiClient.post('/api/notifications/devices', {
            appId: result.appId, deviceId, enabled: true, platform: 'ios', pushProvider: 'apns',
            pushToken: result.token, pushType: 'alert'
        }, { retryAuth: false, signal: controller.signal });
        return current() && !response.error && response.status >= 200 && response.status < 300;
    }
    finally {
        clearTimeout(timer);
    }
}
export function getNotificationDeviceId() {
    return getPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY);
}
